<script setup lang="ts">
  // Footer of the history order modal: same visuals as PosV2ViewActions but
  // without the drafts coupling, and with per-button permission gating that
  // mirrors the legacy ModalPosViewOrder model: Editar/Aceptar/Rechazar
  // require OrdersManage (the server rejects them anyway), Cancelar stays for
  // everyone (independently gated by the OrdersCancel admin PIN) and the
  // print menu only needs a printer. Editar is also hidden on
  // Canceled/Declined/Finished (posDrafts.openOrderTab has no status guard).
  import { computed, inject, Ref } from 'vue';
  import {
    useOrderViewActions,
    IPrintMenuItem,
  } from '@/composition/use-order-view-actions';
  import { useAuth } from '@/composition/use-auth';
  import { useModal } from '@/composition/use-modal';
  import { useOrders } from '@/composition/use-orders';
  import { deviceCanEverPrint } from '@/composition/use-printing';
  import { PermissionType } from '@/enums/PermissionType';
  import { IOrder } from '@/interfaces/IOrder';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';

  const MODAL_NAME = 'pos-v2-order-modal';

  const order = inject<Ref<IOrder>>('order')!;

  const auth = useAuth();
  const modals = useModal();
  const ordersHelper = useOrders();

  const {
    updating,
    isReceived,
    canCancel,
    isEditBlocked,
    handleAccept,
    handleReject,
    handleCancel,
    printMenuItems,
    handlePrint,
  } = useOrderViewActions(order);

  const canManageOrders = computed(() =>
    auth.hasPermission(PermissionType.OrdersManage)
  );

  const showEdit = computed(
    () => canManageOrders.value && !isEditBlocked.value
  );

  function handleEdit() {
    const o = order.value;
    // Close first: editOrder either converts to the V2 edit tab (navigates
    // away) or opens the legacy edit modal on top; on failure it toasts.
    // The session modal may sit underneath (drill-in flow) and vue-js-modal
    // dialogs survive route changes — hide it too (no-op when not open).
    modals.hide(MODAL_NAME);
    modals.hide('pos-v2-session-orders');
    ordersHelper.editOrder(o, 'edit');
  }

  function handlePrintItem(item: IPrintMenuItem, close: () => void) {
    close();
    void handlePrint(item);
  }
</script>

<template>
  <footer class="pos-v2-order-modal-actions">
    <PosUiScrollable
      orientation="horizontal"
      variant="overlay"
      theme="page"
      size="sm"
      :hide-scrollbar="true"
      :arrows="true"
      scroll-class="actions-scroll"
    >
      <!-- Left: Edit + Cancel -->
      <div class="actions-group actions-left">
        <PosUiButton
          v-if="showEdit"
          color="primary"
          variant="solid"
          size="lg"
          icon="pencil"
          class="action-btn action-btn--collapsible"
          @click="handleEdit"
        >
          {{ $t('posV2.editOrder') }}
        </PosUiButton>

        <PosUiButton
          v-if="canCancel"
          color="error"
          variant="outline"
          size="lg"
          icon="close-circle-outline"
          class="action-btn action-btn--collapsible"
          :disabled="updating"
          @click="handleCancel"
        >
          {{ $t('posV2.cancelOrder') }}
        </PosUiButton>
      </div>

      <!-- Center: Accept / Reject (Received only, OrdersManage only) -->
      <div v-if="isReceived && canManageOrders" class="actions-group actions-center">
        <PosUiButton
          color="error"
          variant="outline"
          size="lg"
          icon="close"
          class="action-btn"
          :disabled="updating"
          @click="handleReject"
        >
          {{ $t('posV2.reject') }}
        </PosUiButton>
        <PosUiButton
          color="success"
          variant="solid"
          size="lg"
          icon="check"
          class="action-btn"
          :loading="updating"
          @click="handleAccept"
        >
          {{ $t('posV2.accept') }}
        </PosUiButton>
      </div>

      <!-- Right: Print menu (drop-up via the fixed-strategy popover) -->
      <div class="actions-group actions-right">
        <PosUiPopover
          v-if="deviceCanEverPrint"
          placement="top"
          align="end"
          panel-class="print-popover-panel"
        >
          <template #trigger="{ open, toggle }">
            <PosUiButton
              color="neutral"
              variant="outline"
              size="lg"
              icon="printer"
              class="action-btn action-btn--collapsible"
              @click="toggle"
            >
              {{ $t('posV2.print') }}
              <PosUiIcon
                name="chevron-up"
                class="print-chevron"
                :class="{ 'is-open': open }"
              />
            </PosUiButton>
          </template>

          <template #default="{ close }">
            <div class="print-menu-panel">
              <button
                v-for="item in printMenuItems"
                :key="item.labelKey"
                type="button"
                class="print-menu-item"
                @click="handlePrintItem(item, close)"
              >
                <PosUiIcon :name="item.icon" class="item-icon" />
                <span>{{ $t(item.labelKey) }}</span>
              </button>
            </div>
          </template>
        </PosUiPopover>
      </div>
    </PosUiScrollable>
  </footer>
</template>

<style scoped lang="scss">
  .pos-v2-order-modal-actions {
    @apply tw-flex-shrink-0 tw-border-t tw-border-neutral-200 tw-bg-white;

    :deep(.actions-scroll) {
      @apply tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2;
      @apply lg:tw-gap-3 lg:tw-px-4 lg:tw-py-3;
    }

    .actions-group {
      @apply tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2 lg:tw-gap-3;

      &.actions-left {
        @apply tw-flex-1 tw-justify-start;
      }

      &.actions-center {
        @apply tw-flex-1 tw-justify-center;
      }

      &.actions-right {
        @apply tw-flex-1 tw-justify-end;
      }
    }

    ::v-deep .action-btn {
      @apply tw-min-h-[48px] tw-whitespace-nowrap lg:tw-min-h-[52px] lg:tw-px-5;
    }

    // Icon-only below lg (matches the workspace footer behavior).
    .action-btn--collapsible {
      ::v-deep .pos-ui-button-label {
        @apply tw-hidden lg:tw-inline-flex lg:tw-items-center lg:tw-gap-1.5;
      }
    }

    .print-chevron {
      @apply tw-text-lg tw-transition-transform;

      &.is-open {
        @apply tw-rotate-180;
      }
    }

    // Menu-style panel: drop the popover's default padding/width. The extra
    // .pos-ui-popover-panel class matches the popover's own tw-w-72 rule's
    // specificity (0,4,0) so this consumer override wins by source order.
    :deep(.pos-ui-popover-panel.print-popover-panel) {
      @apply tw-w-auto tw-min-w-[190px] tw-p-1;
    }

    .print-menu-panel {
      @apply tw-flex tw-flex-col;

      .print-menu-item {
        @apply tw-flex tw-min-h-[44px] tw-w-full tw-items-center tw-gap-2 tw-rounded-md tw-px-3 tw-text-left tw-text-sm tw-text-neutral-700 tw-transition-colors hover:tw-bg-neutral-50;

        .item-icon {
          @apply tw-text-lg tw-text-neutral-500;
        }
      }
    }
  }
</style>
